import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { GlobalService } from 'src/app/api/global.service';
import { EmailTypes } from 'src/app/secure-email/EmailTypes';

@Component({
  selector: 'app-email-preview',
  templateUrl: './email-preview.component.html',
})
export class EmailPreviewComponent implements OnInit {
  @Input() type: number = EmailTypes.email;
  @Input() subject: string = '';
  @Input() description: string = '';
  @Input() body: string = '';

  @Input() contact: any = null;
  @Input() company: any = null;
  @Input() project: any = null;
  @Input() task: any = null;
  @Input() action: any = null;

  activeTab: 'outgoing' | 'internal' = 'outgoing';
  EmailTypes = EmailTypes;

  constructor(private modalController: ModalController, public globalService: GlobalService) {}

  ngOnInit() {}

  Close() {
    this.modalController.dismiss();
  }

  get firmName(): string {
    return this.globalService.GetCurrentUser()?.firm_name || 'Your Firm';
  }

  get contactDisplayName(): string {
    return this.contact?.display_name || this.contact?.first_name || 'Example Client';
  }

  get typeDescription(): string {
    switch (+this.type) {
      case EmailTypes.document_request:    return 'a document request';
      case EmailTypes.signature_request:   return 'a signature request';
      case EmailTypes.approval_request:    return 'an approval request';
      case EmailTypes.survey:              return 'a form request';
      case EmailTypes.appointment_request: return 'a request to make an appointment';
      case EmailTypes.question_request:    return 'a question';
      case EmailTypes.checklist_request:   return 'a checklist to complete';
      default:                             return 'a secure email';
    }
  }

  get ctaLabel(): string {
    switch (+this.type) {
      case EmailTypes.document_request:    return 'View Your Document Request';
      case EmailTypes.approval_request:    return 'View Your Approval Request';
      case EmailTypes.appointment_request: return 'Book an Appointment';
      case EmailTypes.question_request:    return 'View Your Question Request';
      case EmailTypes.checklist_request:   return 'View Your Checklist';
      default:                             return 'View Your Email';
    }
  }

  get typeTitle(): string {
    switch (+this.type) {
      case EmailTypes.document_request:    return 'Document Request';
      case EmailTypes.signature_request:   return 'Signature Request';
      case EmailTypes.approval_request:    return 'Approval Request';
      case EmailTypes.survey:              return 'Form Request';
      case EmailTypes.appointment_request: return 'Appointment Request';
      case EmailTypes.question_request:    return 'Question Request';
      case EmailTypes.checklist_request:   return 'Checklist Request';
      default:                             return 'Secure Email';
    }
  }

  get resolvedSubject(): string { return this.resolve(this.subject); }
  get resolvedDescription(): string { return this.resolve(this.description); }
  get resolvedBody(): string { return this.resolve(this.body); }

  resolve(text: string): string {
    if (!text) return text;
    const c = this.contact;
    const co = this.company;
    const p = this.project;

    const r = (field: string, value: string) => {
      text = text.split(field).join(value || '');
    };

    // Contact / client fields (both prefixes)
    if (c) {
      const fiscalYearEnd = this.fmtDate(c.year_end_date ?? c.fiscal_year_end);
      const dob          = this.fmtDate(c.birthday ?? c.date_of_birth);
      const anniversary  = this.fmtDate(c.anniversary);
      for (const prefix of ['contact', 'client']) {
        r(`${prefix}.display_name`,    c.display_name   || '');
        r(`${prefix}.email`,           c.email          || '');
        r(`${prefix}.first_name`,      c.first_name     || '');
        r(`${prefix}.last_name`,       c.last_name      || '');
        r(`${prefix}.middle_name`,     c.middle_name    || '');
        r(`${prefix}.title`,           c.title          || '');
        r(`${prefix}.suffix`,          c.suffix         || '');
        r(`${prefix}.phone`,           c.phone_number   || '');
        r(`${prefix}.cell_phone`,      c.cell_phone     || '');
        r(`${prefix}.alt_phone`,       c.alt_phone      || '');
        r(`${prefix}.fax`,             c.fax            || '');
        r(`${prefix}.address`,         c.str_address    || '');
        r(`${prefix}.city`,            c.city           || '');
        r(`${prefix}.province`,        c.province       || '');
        r(`${prefix}.country`,         c.country        || '');
        r(`${prefix}.postal_code`,     c.postal_code    || '');
        r(`${prefix}.website`,         c.website        || '');
        r(`${prefix}.custom_id`,       c.custom_id      || '');
        r(`${prefix}.gender`,          c.gender         || '');
        r(`${prefix}.date_of_birth`,   dob);
        r(`${prefix}.anniversary`,     anniversary);
        r(`${prefix}.sin`,             c.sin            || '');
        r(`${prefix}.spoken_language`, c.spoken_language || '');
        r(`${prefix}.fiscal_year_end`, fiscalYearEnd);
        r(`${prefix}.notes`,           c.notes          || '');
        r(`${prefix}.description`,     c.description    || '');
      }
    }

    // Company fields
    if (co) {
      const fiscal = this.fmtDate(co.year_end_date ?? co.fiscal_year_end);
      r('company.name',            co.name              || '');
      r('company.phone',           co.phone_number      || '');
      r('company.fax',             co.fax               || '');
      r('company.website',         co.website           || '');
      r('company.address',         co.str_address_1     || '');
      r('company.address_2',       co.str_address_2     || '');
      r('company.city',            co.city              || '');
      r('company.province',        co.province          || '');
      r('company.postal_code',     co.postal_code       || '');
      r('company.country',         co.country           || '');
      r('company.corp_name',       co.corp_name         || '');
      r('company.business_number', co.business_number   || '');
      r('company.fiscal_year_end', fiscal);
      r('company.type',            co.type              || '');
      r('company.industry',        co.industry          || '');
      r('company.custom_id',       co.custom_id         || '');
      r('company.description',     co.description       || '');
    }

    // Project fields
    if (p) {
      r('project.title',       p.title       || '');
      r('project.description', p.description || '');
      this.resolveDateGroup(text, 'project.internal_start', p.internal_start_date, (t) => { text = t; });
      this.resolveDateGroup(text, 'project.internal_end',   p.internal_end_date,   (t) => { text = t; });
      this.resolveDateGroup(text, 'project.client_start',   p.external_start_date, (t) => { text = t; });
      this.resolveDateGroup(text, 'project.client_end',     p.external_end_date,   (t) => { text = t; });
      this.resolveDateGroup(text, 'project.period_start',   p.period_start_date,   (t) => { text = t; });
      this.resolveDateGroup(text, 'project.period_end',     p.period_end_date,     (t) => { text = t; });
      // Patch: after resolveDateGroup may have mutated text via callback
      text = text
        .split('project.internal_start_date').join(this.fmtDate(p.internal_start_date))
        .split('project.internal_end_date').join(this.fmtDate(p.internal_end_date))
        .split('project.client_start_date').join(this.fmtDate(p.external_start_date))
        .split('project.client_end_date').join(this.fmtDate(p.external_end_date))
        .split('project.period_start_date').join(this.fmtDate(p.period_start_date))
        .split('project.period_end_date').join(this.fmtDate(p.period_end_date));
    }

    // Task fields
    const t = this.task;
    if (t) {
      r('task.title',       t.title       || '');
      r('task.description', t.description || '');
      this.resolveDateGroup(text, 'task.start', t.start_date, (v) => { text = v; });
      this.resolveDateGroup(text, 'task.due',   t.due_date,   (v) => { text = v; });
      text = text
        .split('task.start_date').join(this.fmtDate(t.start_date))
        .split('task.due_date').join(this.fmtDate(t.due_date));
    }

    // Action / step fields
    const a = this.action;
    if (a) {
      for (const prefix of ['action', 'step']) {
        r(`${prefix}.title`,       a.title       || '');
        r(`${prefix}.description`, a.description || '');
      }
    }

    // Firm and user
    const user = this.globalService.GetCurrentUser();
    text = text.split('firm.name').join(this.firmName);
    text = text.split('user.display_name').join(user?.display_name || '');
    text = text.split('user.email').join(user?.email || '');

    return text;
  }

  private resolveDateGroup(text: string, prefix: string, dateStr: string, set: (t: string) => void) {
    if (!dateStr) return;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return;
    text = text
      .split(`${prefix}_month`).join(d.toLocaleString('en-US', { month: 'long' }))
      .split(`${prefix}_day`).join(String(d.getDate()))
      .split(`${prefix}_year`).join(String(d.getFullYear()));
    set(text);
  }

  private fmtDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}-${dd}-${d.getFullYear()}`;
  }
}
